import { VenueStorefrontBookingTracking } from '../venue-storefront-booking-flow/venue-storefront-booking-tracking.service'
import { NgOptimizedImage, NgTemplateOutlet } from '@angular/common'
import {
    ChangeDetectionStrategy,
    Component,
    Input,
    OnChanges,
} from '@angular/core'
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { QuillViewComponent } from 'ngx-quill'
import { take } from 'rxjs'
import { Event } from '../../../domain/Event'
import { Toast, ToastType } from '../../../domain/Toast'
import { AddressPipe } from '../../../pipes/address.pipe'
import { PhoneNumberPipe } from '../../../pipes/phone-number.pipe'
import { OpeningHoursPipe } from '../../../pipes/opening-hours.pipe'
import { ToastService } from '../../../services/toast.service'
import { richTextPlainText, truncateRichTextHtml } from '../../../utilities/rich-text'
import { Venue } from '../../../domain/Venue'
import { environment } from '../../../../environments/environment'
import { BookingSubmissionSession } from '../../../services/booking-submission-session.service'
import { VenueStorefrontBookingFlowComponent } from '../venue-storefront-booking-flow/venue-storefront-booking-flow.component'
import { VenueStorefrontBookingFlow } from '../venue-storefront-booking-flow/venue-storefront-booking-flow'
import type { VenueStorefrontBookingSearchInitialValues } from '../venue-storefront-booking-flow/venue-storefront-booking-flow.types'
import { VenueStorefrontEventCardComponent } from '../venue-storefront-event-card/venue-storefront-event-card.component'
import { VenueStorefrontEventModalComponent } from '../venue-storefront-event-modal/venue-storefront-event-modal.component'

interface UpcomingEvent {
    event: Event
    nextDateTime: Date
}

@Component({
    selector: 'app-venue-storefront',
    standalone: true,
    imports: [
        NgOptimizedImage,
        NgTemplateOutlet,
        OpeningHoursPipe,
        PhoneNumberPipe,
        QuillViewComponent,
        VenueStorefrontBookingFlowComponent,
        VenueStorefrontEventCardComponent,
    ],
    providers: [BookingSubmissionSession, VenueStorefrontBookingTracking, VenueStorefrontBookingFlow],
    templateUrl: './venue-storefront.component.html',
    styleUrls: ['./venue-storefront.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VenueStorefrontComponent implements OnChanges {
    @Input({ required: true }) venue!: Venue
    @Input() initialBookingSearch: VenueStorefrontBookingSearchInitialValues | null = null
    @Input() setupBookingRequestId: string | null = null
    @Input() setupCancelled = false
    @Input() paymentIntentId: string | null = null
    @Input() setupIntentId: string | null = null
    @Input() voucherStorefrontPath: string | null = null

    protected mapUrl: SafeResourceUrl | null = null
    protected directionsUrl: string | null = null
    protected upcomingEvents: UpcomingEvent[] = []
    protected descriptionContent: string | null = null
    protected descriptionCanTruncate = false
    protected descriptionExpanded = false
    protected openingHoursExpanded = false
    protected formattedAddress = ''
    protected cityAndRegion = ''
    private readonly descriptionCharacterLimit = 350

    protected get promotionalImageIds(): string[] {
        return this.venue.promotionalImageIds.slice(0, 2)
    }

    protected get bookingPolicyPath(): string | null {
        return this.venue.venueSlug && (
            richTextPlainText(this.venue.bookingPolicy) ||
            richTextPlainText(this.venue.depositRefundPolicy) ||
            richTextPlainText(this.venue.privacyPolicy) ||
            this.venue.paymentsAccount
        )
            ? `/discover/${encodeURIComponent(this.venue.venueSlug)}/booking-policy`
            : null
    }

    protected promotionalImageUrl(imageId: string): string {
        return `${environment.cdnUrl}/1200x675/venue-promotional-images/${imageId}`
    }

    constructor(
        private addressPipe: AddressPipe,
        private domSanitizer: DomSanitizer,
        private modalService: NgbModal,
        private toastService: ToastService,
        private readonly tracking: VenueStorefrontBookingTracking,
        protected readonly bookingFlow: VenueStorefrontBookingFlow
    ) {}

    ngOnChanges() {
        this.descriptionExpanded = false
        this.openingHoursExpanded = false
        this.formattedAddress = this.addressPipe.transform(this.venue.address)
        const region = this.venue.address.county || this.venue.address.country
        this.cityAndRegion = [this.venue.address.city, region].join(', ')
        this.updateDescriptionContent()
        this.bookingFlow.configure(
            this.venue,
            this.initialBookingSearch,
            this.setupBookingRequestId,
            this.setupCancelled,
            this.paymentIntentId,
            this.setupIntentId
        )
        this.upcomingEvents = this.makeUpcomingEvents(new Date())

        const address = this.venue.address
        const encodedAddress = encodeURIComponent(
            [
                address.addressLineOne,
                address.addressLineTwo,
                address.city,
                address.postCode,
                address.country,
            ]
                .filter(Boolean)
                .join(', ')
        )
        const mapUrl = 'https://www.google.com/maps/embed/v1/place' +
            `?key=${environment.googleMapsApiKey}&q=${encodedAddress}`

        this.mapUrl = this.domSanitizer.bypassSecurityTrustResourceUrl(mapUrl)
        this.directionsUrl = environment.getDirectionsURL + encodedAddress
    }

    protected toggleDescription() {
        if (!this.descriptionCanTruncate) {
            return
        }
        this.descriptionExpanded = !this.descriptionExpanded
        this.updateDescriptionContent()
    }

    protected toggleOpeningHours() {
        this.openingHoursExpanded = !this.openingHoursExpanded
    }

    protected copyAddress() {
        void this.copyToClipboard(this.formattedAddress, 'Address copied to clipboard.')
    }

    protected async shareVenue() {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: this.venue.displayName,
                    url: window.location.href,
                })
            } catch (error) {
                if (!(error instanceof DOMException && error.name === 'AbortError')) {
                    this.showToast(
                        'The venue could not be shared. Please try again.',
                        'Unable to share',
                        ToastType.Danger
                    )
                }
            }
            return
        }

        await this.copyToClipboard(window.location.href, 'Venue link copied to clipboard.')
    }

    protected get todayDayOfWeek(): number {
        return new Date().getDay() || 7
    }

    protected openEventModal(upcomingEvent: UpcomingEvent) {
        const context = {
            eventId: upcomingEvent.event.id,
            eventName: upcomingEvent.event.displayName,
        }
        this.tracking.record('choice_viewed', context)
        const modal = this.modalService.open(VenueStorefrontEventModalComponent, {
            ariaLabelledBy: 'venue-storefront-event-modal-title',
            centered: true,
            fullscreen: 'sm',
            scrollable: true,
            size: 'lg',
        })
        modal.dismissed.pipe(take(1)).subscribe(() => this.tracking.record('choice_dismissed', context))
        const component = modal.componentInstance as VenueStorefrontEventModalComponent
        component.configure(
            upcomingEvent.event,
            upcomingEvent.nextDateTime,
            this.venue.currencyCode
        )
        if (upcomingEvent.event.notReservableOnline) {
            return
        }
        component.enableBookingSelection()
        modal.closed
            .pipe(take(1))
            .subscribe(() => this.bookingFlow.startEventBooking(
                upcomingEvent.event,
                upcomingEvent.nextDateTime
            ))
    }

    private makeUpcomingEvents(referenceDate: Date): UpcomingEvent[] {
        return this.venue.events
            .map(event => this.makeUpcomingEvent(event, referenceDate))
            .filter((event): event is UpcomingEvent => event !== null)
            .sort((first, second) => {
                const dateOrder = first.nextDateTime.getTime() - second.nextDateTime.getTime()
                return dateOrder || first.event.displayOrder - second.event.displayOrder
            })
            .slice(0, 2)
    }

    private async copyToClipboard(value: string, successMessage: string) {
        try {
            await navigator.clipboard.writeText(value)
            this.showToast(successMessage, 'Copied', ToastType.Success)
        } catch {
            this.showToast(
                'Your browser could not copy this automatically. Please copy it manually.',
                'Unable to copy',
                ToastType.Danger
            )
        }
    }

    private showToast(message: string, header: string, type: ToastType) {
        const toast = new Toast(message)
        toast.header = header
        toast.type = type
        this.toastService.show(toast)
    }

    private updateDescriptionContent() {
        const description = this.venue.description
        if (!description) {
            this.descriptionContent = null
            this.descriptionCanTruncate = false
            return
        }
        this.descriptionCanTruncate = richTextPlainText(description).length >
            this.descriptionCharacterLimit
        this.descriptionContent = this.descriptionExpanded
            ? description
            : truncateRichTextHtml(description, this.descriptionCharacterLimit)
    }

    private makeUpcomingEvent(event: Event, referenceDate: Date): UpcomingEvent | null {
        const nextDateTime = event.recurrence.nextDateTimeOnOrAfter(referenceDate)
        if (nextDateTime === null) {
            return null
        }
        return {
            event,
            nextDateTime,
        }
    }
}
